// src/pages/markets/board/api/useActivity.ts

import { useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { fetchBoardActivity, MAX_ACTIVITY_EVENT_IDS } from './boardApi';
import { boardKeys, filterSignature, idsKey } from './boardKeys';
import type { BoardFilters, EventActivity } from './boardTypes';

export interface UseActivityOptions {
    eventIds: string[];
    filters: BoardFilters;
    filterKeys?: readonly string[] | null;
    enabled?: boolean;
}

export interface ActivityResult {
    byEvent: Map<string, EventActivity>;
    scope: string;
    metricMode: string;
    unit: string;
    windows: Record<string, string>;
    isFetching: boolean;
    isLoading: boolean;
    error: Error | null;
}

const EMPTY = new Map<string, EventActivity>();
const NO_WINDOWS: Record<string, string> = {};

const ACTIVITY_STALE_MS = 55_000;
const ACTIVITY_GC_MS = 10 * 60_000;

// Batched over the visible window, not per card: a column of thirty cards is
// one request, and the minute tick refetches it once.
export function useActivity(opts: UseActivityOptions): ActivityResult {
    const { filters, filterKeys = null, enabled = true } = opts;
    const ids = opts.eventIds.slice(0, MAX_ACTIVITY_EVENT_IDS);
    const active = enabled && ids.length > 0;
    const signature = filterSignature(filters);

    const query = useQuery({
        queryKey: boardKeys.activity(idsKey(ids), signature),
        queryFn: ({ signal }) =>
            fetchBoardActivity({ eventIds: ids, filters, filterKeys, signal }),
        enabled: active,
        staleTime: ACTIVITY_STALE_MS,
        gcTime: ACTIVITY_GC_MS,
        placeholderData: keepPreviousData,
    });

    const byEvent = useMemo(() => {
        if (!query.data) return EMPTY;
        return new Map(query.data.activity.map(a => [a.event_id, a]));
    }, [query.data]);

    return {
        byEvent,
        scope: query.data?.scope ?? filters.scope ?? 'market',
        metricMode: query.data?.metric_mode ?? filters.metricMode ?? 'absolute',
        unit: query.data?.unit ?? '',
        windows: query.data?.windows ?? NO_WINDOWS,
        isFetching: query.isFetching,
        isLoading: active && query.isLoading,
        error: (query.error as Error) ?? null,
    };
}